/**
 * Builds the mapping-phase output preview (column-mapping-ui spec, "Output
 * preview table"): a small sample of rows as they will land in the catalog,
 * post-split/post-transform, with per-cell/per-row validation flags.
 *
 * Computed entirely client-side from the columns response's per-column
 * `columnSamples`, reusing the SAME pure, shared `@ooh/contracts` row-mapping
 * and validation engine the API dry-run and worker import use
 * (`buildCustomMappedRow` / `validateRow`) — no reimplementation of transform
 * or validation logic here. `columnSamples` samples are independently
 * collected per column (see import-xlsx.helpers.ts#collectSampleValues), so
 * zipping them by index is a best-effort approximation of "a row" — acceptable
 * for a small preview, not a guarantee of exact row alignment.
 */
import { buildCustomMappedRow, validateRow, resolveMappedToColumn } from '@ooh/contracts';
import type { RowError, FieldOptionItem } from '@ooh/contracts';
import type { ColumnMappingItem } from '../components/mapping-phase';
import type {
  MappingTransformsPreviewColumn,
  MappingTransformsPreviewRow,
  PreviewRowStatus,
} from '../components/mapping-transforms-preview-table';
import {
  collectMappedFieldKeys,
  stripRequiredSuffix,
  SUPPLIER_FIELD_KEY,
  isSupplierFieldCovered,
  MEDIA_TYPE_FIELD_KEY,
} from './collect-mapped-field-keys';

export const PREVIEW_ROW_COUNT = 3;

/** FIELD_OPTIONS keys the region/county columns can be derived from when coordinates resolve. */
const REGION_FIELD_KEY = 'region';
const DISTRICT_FIELD_KEY = 'district';

export interface MappingPreviewLabels {
  regionDerivedWarning: string;
  /** Resolves a RowError to a translated message — mirrors validation-tables.tsx's
   * resolveValidationMsg (t('validation.errors.CODE', params), falling back to the
   * pre-translated `message` when no code is present). Keeps @ooh/contracts's RowError
   * messages (English, code-driven) out of the rendered UI. */
  resolveErrorMessage: (error: RowError) => string;
}

function buildPreviewColumns(
  fieldOptions: FieldOptionItem[],
  mappedKeys: Set<string>,
  overrideActive: boolean,
  supplierCovered: boolean,
): { columns: MappingTransformsPreviewColumn[]; derivedKeys: Set<string> } {
  const derivedKeys = new Set<string>();
  if (mappedKeys.has('latitude') && mappedKeys.has('longitude')) {
    if (!mappedKeys.has(REGION_FIELD_KEY)) derivedKeys.add(REGION_FIELD_KEY);
    if (!mappedKeys.has(DISTRICT_FIELD_KEY)) derivedKeys.add(DISTRICT_FIELD_KEY);
  }
  // A required field left entirely unmapped still gets a rendered column so its validateRow
  // error (already collected into allFlags below) surfaces as a visible per-cell reason instead
  // of silently contributing to a bare red row-status icon with no explanation (browser-QA
  // regression: "error rows show no reason"). The standard cell branch in buildRowCells already
  // looks up a RowError by resolved DB column, so no extra branching is needed here.
  const columns = fieldOptions
    .filter((o) => {
      if (mappedKeys.has(o.key) || derivedKeys.has(o.key)) return true;
      if (o.required !== true) return false;
      return !(
        (overrideActive && o.key === MEDIA_TYPE_FIELD_KEY) ||
        isSupplierFieldCovered(o.key, supplierCovered)
      );
    })
    .map((o) => ({ id: o.key, label: stripRequiredSuffix(o.label) }));
  return { columns, derivedKeys };
}

/** Drops the 'name'-required validateRow error when the operator already covered supplier via
 * the combobox/saved mapping — otherwise the row would still surface a false "supplier is
 * required" error/reason even though the rendered 'supplier' column was suppressed above.
 * Resolves the DB column via the canonical FIELD_OPTIONS→DB-column table (resolveMappedToColumn)
 * instead of a hardcoded literal, so this can't silently desync if that mapping ever changes. */
function filterSupplierCoveredErrors(errors: RowError[], supplierCovered: boolean): RowError[] {
  if (!supplierCovered) return errors;
  const supplierDbColumn = resolveMappedToColumn(SUPPLIER_FIELD_KEY);
  return errors.filter((e) => e.field !== supplierDbColumn);
}

/** Zips columnSamples[col][index] across every mapped source column into one pseudo raw row. */
function buildRawRow(
  mappings: ColumnMappingItem[],
  columnSamples: Record<string, string[]>,
  index: number,
): Record<string, string> {
  const rawRow: Record<string, string> = {};
  for (const m of mappings) {
    const sample = columnSamples[m.sourceColumn]?.[index];
    if (sample !== undefined) rawRow[m.sourceColumn] = sample;
  }
  return rawRow;
}

function severityOf(flags: RowError[]): PreviewRowStatus {
  if (flags.some((f) => f.severity === 'error')) return 'error';
  if (flags.length > 0) return 'warning';
  return 'ok';
}

/** DB columns rendered as an actual preview column (derived-region columns don't resolve to a
 * DB column, so they're excluded — their flag is always attached in buildRowCells directly). */
function buildRenderedDbColumns(
  columns: MappingTransformsPreviewColumn[],
  derivedKeys: Set<string>,
): Set<string> {
  const rendered = new Set<string>();
  for (const col of columns) {
    if (derivedKeys.has(col.id)) continue;
    rendered.add(resolveMappedToColumn(col.id));
  }
  return rendered;
}

/** Sentinel field on the synthetic derived-region flag (see buildMappingPreview) — never a
 * real DB column, so it must be excluded from the orphaned-flag reason scan below. */
const DERIVED_REGION_FLAG_FIELD = '__derived_region__';

/**
 * Item #3: a flag whose `field` doesn't resolve to any rendered preview column (e.g. a
 * pattern-transform's `pattern_no_match` warning, keyed by the raw source-column name like
 * "Współrzędne" rather than a DB column) would otherwise silently flip the row status to
 * warning/error with zero visible explanation. Surfaces those as generic per-row reasons,
 * generalizing the same "always show why" intent buildPreviewColumns already applies to a
 * required field left entirely unmapped (which DOES get a rendered column).
 */
function buildRowReasons(
  flags: RowError[],
  renderedDbColumns: Set<string>,
  labels: MappingPreviewLabels,
): string[] {
  return flags
    .filter((f) => f.field !== DERIVED_REGION_FLAG_FIELD && !renderedDbColumns.has(f.field))
    .map((f) => labels.resolveErrorMessage(f));
}

function buildRowCells(
  columns: MappingTransformsPreviewColumn[],
  derivedKeys: Set<string>,
  mapped: Record<string, string | string[] | undefined>,
  flags: RowError[],
  labels: MappingPreviewLabels,
): MappingTransformsPreviewRow['cells'] {
  const flagsByColumn = new Map(flags.map((f) => [f.field, f]));
  const cells: MappingTransformsPreviewRow['cells'] = {};
  for (const col of columns) {
    if (derivedKeys.has(col.id)) {
      cells[col.id] = {
        value: '—',
        flag: { severity: 'warning', message: labels.regionDerivedWarning },
      };
      continue;
    }
    const dbColumn = resolveMappedToColumn(col.id);
    const rawValue = mapped[dbColumn];
    const value = Array.isArray(rawValue) ? rawValue.join(', ') : (rawValue ?? '');
    const rowError = flagsByColumn.get(dbColumn);
    cells[col.id] = {
      value,
      mono: col.id === 'latitude' || col.id === 'longitude',
      ...(rowError
        ? { flag: { severity: rowError.severity, message: labels.resolveErrorMessage(rowError) } }
        : {}),
    };
  }
  return cells;
}

export interface MappingPreview {
  columns: MappingTransformsPreviewColumn[];
  rows: MappingTransformsPreviewRow[];
}

/**
 * Builds up to PREVIEW_ROW_COUNT preview rows for the currently-mapped columns.
 * `overrideActive` mirrors the worker/api's per-file media-type-override gate
 * (skips the media_type-required check the same way validateRow does server-side).
 * `supplierCovered` mirrors use-mapping-phase-state.ts's supplierCoveredByCombobox — true when
 * the operator picked a supplier via the combobox/saved mapping rather than a mapped column.
 */
export function buildMappingPreview(
  mappings: ColumnMappingItem[],
  columnSamples: Record<string, string[]> | undefined,
  fieldOptions: FieldOptionItem[],
  labels: MappingPreviewLabels,
  overrideActive: boolean,
  supplierCovered = false,
): MappingPreview {
  const samples = columnSamples ?? {};
  const mappedKeys = collectMappedFieldKeys(mappings);
  const { columns, derivedKeys } = buildPreviewColumns(
    fieldOptions,
    mappedKeys,
    overrideActive,
    supplierCovered,
  );
  const renderedDbColumns = buildRenderedDbColumns(columns, derivedKeys);

  const rows: MappingTransformsPreviewRow[] = [];
  for (let i = 0; i < PREVIEW_ROW_COUNT; i++) {
    const rawRow = buildRawRow(mappings, samples, i);
    if (Object.keys(rawRow).length === 0) break;

    const transformFlags: RowError[] = [];
    const mapped = buildCustomMappedRow(rawRow, mappings, transformFlags);
    const validationErrors = filterSupplierCoveredErrors(
      validateRow(mapped, overrideActive),
      supplierCovered,
    );
    const derivedFlag: RowError[] =
      derivedKeys.size > 0
        ? [{ field: DERIVED_REGION_FLAG_FIELD, message: '', severity: 'warning' }]
        : [];
    const combinedFlags = [...transformFlags, ...validationErrors];
    const allFlags = [...combinedFlags, ...derivedFlag];
    const reasons = buildRowReasons(combinedFlags, renderedDbColumns, labels);

    rows.push({
      id: `preview-row-${i}`,
      status: severityOf(allFlags),
      cells: buildRowCells(columns, derivedKeys, mapped, combinedFlags, labels),
      ...(reasons.length > 0 ? { reasons } : {}),
    });
  }

  return { columns, rows };
}
